// ============================================================
// ragEngine.js - browser-side RAG orchestrator
//
// Retrieval, answer generation, the answer cache, and the monthly budget (both the hard-cap check
// and charging each query's spend to rag_budget) happen in the rag-query Edge
// Function. The browser owns query/activity logging and UI-facing
// result shaping. API keys never touch the client.
// ============================================================

import { logEvent } from '../lib/activityLogger';
import { createId } from '../utils/createId';

const HOA_STOPWORDS = new Set([
  'hoa', 'homeowners', 'association', 'crestview', 'eighty',
  'shall', 'may', 'upon', 'herein', 'thereof', 'whereby', 'thereto',
  'said', 'such', 'any', 'all', 'each', 'every', 'other', 'this',
  'that', 'with', 'from', 'have', 'will', 'been', 'also', 'into',
  'than', 'its', 'are', 'was', 'for', 'not', 'but', 'and', 'the',
]);

const TEMPORAL_SIGNALS = new Set([
  'january','february','march','april','may','june','july',
  'august','september','october','november','december',
  '2023','2024','2025','2026',
  'resolution','vote','approved','motion','meeting','last','recent','latest',
  'budget','financial','expense','dues','assessment','annual',
]);

const ACC_SIGNALS = new Set([
  'setback','fence','shed','garage','roof','addition','deck','building',
  'structure','paint','color','square','foot','footage','unit','lot',
  'outbuilding','accessory','exterior','siding','metal','height','wall',
]);

const FINANCIAL_SIGNALS = new Set([
  'dues','assessment','fee','budget','reserve','payment','delinquent',
  'balance','fine','penalty','income','expense','cost','charge',
]);

const PROCEDURAL_SIGNALS = new Set([
  'process','procedure','how','approve','approval','submit','application',
  'request','deadline','who','contact','board','vote','committee',
]);

const GOVERNING_SIGNALS = new Set([
  'allowed','allow','prohibit','prohibited','restriction','restrictions','rule','rules',
  'ccr','ccrs','covenant','covenants','bylaw','bylaws','amendment','phase',
  'pet','pets','animal','animals','livestock','poultry','chicken','chickens',
  'goat','goats','dog','dogs','cat','cats','bird','birds',
]);

function normalizeQuery(query) {
  return String(query || '').toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
}

function tokenizeQuery(query) {
  return String(query || '')
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .split(/\s+/)
    .filter(t => t.length > 2 && !HOA_STOPWORDS.has(t));
}

function classifyIntent(terms) {
  const termSet = new Set(terms);
  let temporal = 0, acc = 0, financial = 0, procedural = 0, governing = 0;
  for (const t of termSet) {
    if (TEMPORAL_SIGNALS.has(t)) temporal++;
    if (ACC_SIGNALS.has(t)) acc++;
    if (FINANCIAL_SIGNALS.has(t)) financial++;
    if (PROCEDURAL_SIGNALS.has(t)) procedural++;
    if (GOVERNING_SIGNALS.has(t)) governing++;
  }
  const scores = { temporal, acc, financial, procedural, governing };
  const max = Math.max(temporal, acc, financial, procedural, governing);
  if (max === 0) return 'general';
  return Object.keys(scores).find(k => scores[k] === max);
}

function normalizeHistoryForCache(historyMessages = []) {
  if (!Array.isArray(historyMessages) || historyMessages.length === 0) return '';
  return historyMessages
    .slice(-6)
    .map((message) => `${message.role || ''}:${normalizeQuery(message.content || '').slice(0, 240)}`)
    .join('|');
}

function normalizeCitations(sources = []) {
  if (!Array.isArray(sources)) return [];
  return sources.map((source) => {
    if (typeof source === 'string') {
      return { document_name: source };
    }
    return source;
  }).filter(Boolean);
}

// The answer cache lives in the rag-query Edge Function (2026-09-24), keyed on the
// caller's verified search scope. It used to live here, keyed on the question
// alone, which let a board-scope answer be served to a homeowner — and RLS let
// any member read or plant cached answers. The browser no longer touches
// rag_query_cache.
//
// options.includeDocumentId — Level 5 stress test only (admin sessions; enforced
// server-side in rag-query). Searches one not-yet-approved document alongside the
// live corpus; rag-query never caches those answers, and they stay out of the
// homeowner query log that feeds the self-improvement loop.
export async function runRAGQuery(supabase, hoaId, userId, userRole, query, historyMessages = [], threadId = null, options = {}) {
  const includeDocumentId = options.includeDocumentId || null;
  const historyFingerprint = normalizeHistoryForCache(historyMessages);
  const baseTerms = tokenizeQuery(`${query} ${historyFingerprint}`);
  const intentClass = classifyIntent(baseTerms);

  const { data: edgeData, error: edgeError } = await supabase.functions.invoke(
    'rag-query',
    {
      body: {
        query,
        hoa_id: hoaId,
        user_role: userRole || 'member',
        intent_class: intentClass,
        history_messages: historyMessages,
        ...(includeDocumentId ? { include_document_id: includeDocumentId } : {}),
      },
    }
  );

  if (edgeError) throw new Error(`RAG Edge Function error: ${edgeError.message}`);
  if (!edgeData?.answer) throw new Error('Invalid response from RAG service');

  // Monthly cap reached (enforced server-side). Nothing was spent, and this
  // answer must never be cached — it would keep being served after the budget
  // resets or the limit is raised.
  if (edgeData.budget_exceeded) {
    return {
      logId: null, // not logged, so it cannot be rated
      answer: edgeData.answer,
      sources: [],
      citations: [],
      confidence: 'low',
      cacheHit: false,
      budgetExceeded: true,
    };
  }

  const answer = edgeData.answer;
  const citations = normalizeCitations(edgeData.citations || edgeData.sources || []);
  const sources = edgeData.sources || citations.map((citation) => citation.document_name).filter(Boolean);
  const inputTokens = edgeData.usage?.input_tokens || 0;
  const outputTokens = edgeData.usage?.output_tokens || 0;
  const chunksUsed = edgeData.chunks_used || 0;
  const docsSearched = edgeData.docs_searched || 0;
  const chunkIds = edgeData.chunk_ids || null;
  const retrievalQuery = edgeData.retrieval_query || query;
  const cacheHit = Boolean(edgeData.cache_hit);

  // Full per-query cost (Claude rewrite + answer, Voyage embed + rerank), computed
  // and already charged to rag_budget by the Edge Function. Logged here as-is.
  const totalCost = Number(edgeData.cost_usd) || 0;

  logEvent('rag_query', {
    query: query.substring(0, 200),
    intent_class: intentClass,
    answer_length: answer.length,
    chunks_used: chunksUsed,
    cache_hit: cacheHit,
    cost_usd: totalCost,
    rewrite_used: Boolean(edgeData.rewrite_used),
  });

  // The id is returned to the caller so thumbs up/down can address this exact
  // row (rate_rag_answer). Stress-test queries are not logged and get no id.
  const logId = includeDocumentId ? null : createId();
  if (logId) supabase.from('rag_query_log').insert({
    id: logId,
    hoa_id: hoaId,
    user_id: userId,
    user_role: userRole,
    query,
    answer,
    input_tokens: inputTokens,
    output_tokens: outputTokens,
    estimated_cost_usd: totalCost,
    intent_class: intentClass,
    chunks_used: chunksUsed,
    docs_returned: docsSearched,
    cache_hit: cacheHit,
    thread_id: threadId,
    chunk_ids: chunkIds,
  }).then(({ error }) => { if (error) console.error('rag_query_log insert failed:', error.message); });

  return {
    logId,
    answer,
    sources,
    citations,
    confidence: citations.length > 0 ? 'high' : 'low',
    cacheHit,
    docsSearched,
    chunksEvaluated: chunksUsed,
    chunksUsed,
    intentClass,
    retrievalQuery,
    rewriteUsed: Boolean(edgeData.rewrite_used),
    tokens: { input: inputTokens, output: outputTokens },
    costUsd: totalCost,
  };
}

